import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Checkbox, Input, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faMagnifyingGlass, faXmark } from '@fortawesome/pro-solid-svg-icons';
import type { Editor } from '@tiptap/react';

interface NoteFindReplaceProps {
  editor: Editor;
  editable: boolean;
  focusRequest: number;
  onClose: () => void;
}

const NoteFindReplace: React.FC<NoteFindReplaceProps> = ({ editor, editable, focusRequest, onClose }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const searchInput = useRef<InputRef>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState(editor.storage.findAndReplace.searchTerm || '');
  const [replaceTerm, setReplaceTerm] = useState(editor.storage.findAndReplace.replaceTerm || '');
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(Boolean(editor.storage.findAndReplace.caseSensitive));
  const [wholeWord, setWholeWord] = useState(Boolean(editor.storage.findAndReplace.wholeWord));
  const [footerBounds, setFooterBounds] = useState<{ left: number; width: number } | null>(null);
  const [, setTransactionRevision] = useState(0);

  useEffect(() => {
    previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      editor.commands.clearSearch();
      const target = previousFocus.current;
      if (target?.isConnected) target.focus();
      else editor.commands.focus();
    };
  }, [editor]);

  useEffect(() => {
    const refresh = () => setTransactionRevision(value => value + 1);
    editor.on('transaction', refresh);
    return () => { editor.off('transaction', refresh); };
  }, [editor]);

  useEffect(() => {
    requestAnimationFrame(() => {
      searchInput.current?.focus();
      searchInput.current?.select();
    });
  }, [focusRequest]);

  useLayoutEffect(() => {
    const host = panelRef.current?.closest('.library-item-view');
    if (!(host instanceof HTMLElement)) return;
    const measure = () => {
      const bounds = host.getBoundingClientRect();
      setFooterBounds({ left: bounds.left, width: bounds.width });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const storage = editor.storage.findAndReplace;
  const resultCount = storage.results.length;
  const currentResult = resultCount && storage.currentIndex !== null ? storage.currentIndex + 1 : 0;
  const resultLabel = !searchTerm ? '' : resultCount ? `${currentResult} of ${resultCount}` : 'No results';

  const updateSearch = (value: string) => {
    setSearchTerm(value);
    editor.commands.setSearchTerm(value);
  };

  const updateReplace = (value: string) => {
    setReplaceTerm(value);
    editor.commands.setReplaceTerm(value);
  };

  const toggleCaseSensitive = () => {
    const next = !caseSensitive;
    setCaseSensitive(next);
    editor.commands.setCaseSensitive(next);
  };

  const toggleWholeWord = () => {
    const next = !wholeWord;
    setWholeWord(next);
    editor.commands.setWholeWord(next);
  };

  const scrollCurrentIntoView = () => {
    const currentMatch = editor.view.dom.querySelector('.find-and-replace-result-current');
    if (currentMatch instanceof HTMLElement && typeof currentMatch.scrollIntoView === 'function') {
      currentMatch.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  };

  const navigate = (direction: 'previous' | 'next') => {
    if (!searchTerm) return;
    if (direction === 'previous') editor.commands.goToPreviousResult();
    else editor.commands.goToNextResult();
    scrollCurrentIntoView();
  };

  const replaceCurrent = () => {
    editor.commands.replace();
    scrollCurrentIntoView();
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      navigate(event.shiftKey ? 'previous' : 'next');
    }
  };

  const handlePanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
  };

  return (
    <div
      ref={panelRef}
      className={`library-find-panel ${replaceOpen ? 'is-replacing' : ''}`}
      role="search"
      aria-label="Find in note"
      onKeyDown={handlePanelKeyDown}
      style={footerBounds || undefined}
    >
      <div className="library-find-row">
        <Input
          ref={searchInput}
          aria-label="Find in note"
          prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
          placeholder="Find in note"
          value={searchTerm}
          onChange={event => updateSearch(event.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        <span className="library-find-count" aria-live="polite">{resultLabel}</span>
        <div className="library-find-options" aria-label="Search options">
          <Checkbox checked={caseSensitive} onChange={toggleCaseSensitive}>Match case</Checkbox>
          <Checkbox checked={wholeWord} onChange={toggleWholeWord}>Whole words</Checkbox>
        </div>
        <Tooltip title="Previous result · Shift Enter">
          <Button aria-label="Previous result" type="text" disabled={!resultCount} icon={<FontAwesomeIcon icon={faChevronUp} />} onClick={() => navigate('previous')} />
        </Tooltip>
        <Tooltip title="Next result · Enter">
          <Button aria-label="Next result" type="text" disabled={!resultCount} icon={<FontAwesomeIcon icon={faChevronDown} />} onClick={() => navigate('next')} />
        </Tooltip>
        {editable && <Button type="text" className="library-find-replace-toggle" onClick={() => setReplaceOpen(value => !value)}>{replaceOpen ? 'Find' : 'Replace'}</Button>}
        <Tooltip title="Close · Esc">
          <Button aria-label="Close find" type="text" icon={<FontAwesomeIcon icon={faXmark} />} onClick={onClose} />
        </Tooltip>
      </div>
      {editable && replaceOpen && (
        <div className="library-find-row library-replace-row">
          <Input
            aria-label="Replace with"
            placeholder="Replace with"
            value={replaceTerm}
            onChange={event => updateReplace(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && resultCount) {
                event.preventDefault();
                replaceCurrent();
              }
            }}
          />
          <Button disabled={!resultCount} onClick={replaceCurrent}>Replace</Button>
          <Button disabled={!resultCount} onClick={() => editor.commands.replaceAll()}>Replace all</Button>
        </div>
      )}
    </div>
  );
};

export default NoteFindReplace;
